/**
 * Organization Mutations Hook
 *
 * Uses tRPC for secure organization updates through backend.
 * Credentials are written to Supabase Vault (encrypted at rest) via service role.
 */

import { useLogger } from '@/lib/logger';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc, type RouterInputs } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';

type UpdateOrganizationInput = RouterInputs['organization']['updateOrganization'];

export function useOrganizationMutations() {
  const { t } = useTranslation('settings');
  const logger = useLogger();
  const { organization, reloadOrganization } = useOrganization();
  const utils = trpc.useUtils();

  const updateOrganization = trpc.organization.updateOrganization.useMutation({
    onMutate: (updates: UpdateOrganizationInput) => {
      logger.info('Updating organization settings', {
        operation: 'update_organization',
        organizationId: organization?.id,
        updates: Object.keys(updates),
      });
    },
    onSuccess: (_result, variables: UpdateOrganizationInput) => {
      // Invalidate tRPC query and reload OrganizationContext
      utils.organization.getOrganization.invalidate();
      reloadOrganization();

      logger.info('Organization updated successfully', {
        operation: 'update_organization',
      });

      // Check if Shopify credentials were updated
      const updatedShopify =
        'shopifyDomain' in variables ||
        'shopifyAccessToken' in variables;

      toast({
        title: t('toast.settingsUpdated'),
        description: updatedShopify
          ? t('toast.shopifyCredentialsUpdated')
          : t('toast.orgSettingsUpdated'),
      });
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'organization-settings',
          operation: 'update_organization',
        },
      });

      logger.error('Failed to update organization', {
        operation: 'update_organization',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('toast.failedUpdateOrg'),
        variant: 'destructive',
      });
    },
  });

  const runSync = trpc.organization.runSync.useMutation({
    onMutate: (input) => {
      logger.info('Starting manual sync', {
        operation: 'run_sync',
        organizationId: organization?.id,
        lookbackMinutes: input?.lookbackMinutes,
      });
    },
    onSuccess: (result) => {
      utils.organization.getOrganization.invalidate();
      utils.shopifyIntegration.getOrderSyncState.invalidate();

      logger.info('Manual sync started successfully', {
        operation: 'run_sync',
        startedAt: result.startedAt,
      });

      toast({
        title: t('toast.manualSyncStarted'),
        description: t('toast.manualSyncDescription'),
      });
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'organization-settings',
          operation: 'run_sync',
        },
      });

      logger.error('Failed to start manual sync', {
        operation: 'run_sync',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('toast.failedManualSync'),
        variant: 'destructive',
      });
    },
  });

  const runPapProductSync = trpc.organization.runPapProductSync.useMutation({
    onMutate: () => {
      logger.info('Starting manual PAP product sync', {
        operation: 'run_pap_product_sync',
        organizationId: organization?.id,
      });
    },
    onSuccess: (result) => {
      logger.info('Manual PAP product sync started', {
        operation: 'run_pap_product_sync',
        startedAt: result.startedAt,
      });

      toast({
        title: t('toast.papSyncStarted'),
        description: t('toast.papSyncDescription'),
      });
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'organization-settings',
          operation: 'run_pap_product_sync',
        },
      });

      logger.error('Failed to start PAP product sync', {
        operation: 'run_pap_product_sync',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('toast.failedPapSync'),
        variant: 'destructive',
      });
    },
  });

  const backfillInventory = trpc.organization.backfillInventory.useMutation({
    onMutate: () => {
      logger.info('Starting Shopify inventory ID backfill', {
        operation: 'backfill_inventory_ids',
        organizationId: organization?.id,
      });
    },
    onSuccess: (result) => {
      toast({
        title: t('toast.inventoryIdsUpdated'),
        description: t('toast.inventoryIdsDescription', { updated: result.updatedCount, remaining: result.remaining }),
      });

      logger.info('Inventory ID backfill completed', {
        operation: 'backfill_inventory_ids',
        updated: result.updatedCount,
        remaining: result.remaining,
      });
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'organization-settings',
          operation: 'backfill_inventory_ids',
        },
      });

      logger.error('Failed to backfill inventory item IDs', {
        operation: 'backfill_inventory_ids',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('toast.failedBackfill'),
        variant: 'destructive',
      });
    },
  });

  const backfillUnmatchedSkus = trpc.organization.backfillUnmatchedSkus.useMutation({
    onMutate: (input) => {
      logger.info('Starting unmatched SKU backfill', {
        operation: 'backfill_unmatched_skus',
        organizationId: organization?.id,
        locationId: input.locationId ?? undefined,
      });

      toast({
        title: t('toast.startingBackfill'),
        description: t('toast.backfillDescription'),
      });
    },
    onSuccess: (result) => {
      logger.info('Unmatched SKU backfill started', {
        operation: 'backfill_unmatched_skus',
        startedAt: result.startedAt,
      });
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'organization-settings',
          operation: 'backfill_unmatched_skus',
        },
      });

      logger.error('Failed to start unmatched SKU backfill', {
        operation: 'backfill_unmatched_skus',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('toast.failedBackfillUnmatched'),
        variant: 'destructive',
      });
    },
  });

  return {
    updateOrganization,
    runSync,
    runPapProductSync,
    backfillInventory,
    backfillUnmatchedSkus,
  };
}
